import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { pathToSettingsTeamEdit } from '@/navigation/navigate';
import { teamService } from '@/services/team-service';
import type { Team } from '@/types/core/team';
import { pollForResource } from '@/utils/poll';
import type { UUID } from '@/utils/uuid';

import { teamMutationKeys, teamQueryKeys } from './query-keys';

/**
 * Hook to create a team.
 * Returns a react-query mutation that accepts `{ name: string; description?: string }`.
 * Invalidates the teams cache to trigger a refetch.
 */
export function useCreateTeam(tenantId?: UUID, tenantSlug?: string) {
  const qc = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationKey: teamMutationKeys.createTeam(tenantId),
    mutationFn: async (payload: { name: string; description?: string }) => {
      if (!tenantId) throw new Error('Missing tenant id');

      const teamId = await teamService.createTeam(tenantId, payload);

      const team = await pollForResource(() =>
        teamService.findTeam(tenantId, teamId)
      );

      return team ?? { teamId };
    },
    onSuccess: (team: Team) => {
      qc.invalidateQueries({
        queryKey: teamQueryKeys.listByTenant(tenantId),
        refetchType: 'all',
      });

      if (tenantSlug && team.teamId) {
        navigate(pathToSettingsTeamEdit(tenantSlug, team.teamId));
      }
    },
  });
}
